import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { buildApiUrl, getDefaultHeaders, Config } from '../../../services/config';
import ViewLayout from '../../../components/Common/ViewLayout';

interface ObraDetalhada {
  id: number;
  numero_contrato: string;
  numero_obra: string;
  cliente_nome: string;
  cliente_cnpj: string;
  cliente_endereco: string;
  cliente_contato: string;
  cliente_email: string;
  nome: string;
  descricao: string;
  categoria: string;
  endereco_logradouro: string;
  endereco_bairro: string;
  endereco_cidade: string;
  endereco_estado: string;
  endereco_cep: string;
  latitude: number;
  longitude: number;
  valor_total: number;
  valor_real: number;
  percentual_conclusao: number;
  moeda: string;
  status: string;
  data_inicio_prevista: string;
  data_inicio_real: string;
  data_fim_prevista: string;
  data_fim_real: string;
  data_cadastro: string;
  data_atualizacao: string;
  observacoes: string;
  responsavel_tecnico_nome: string;
  responsavel_tecnico_contato: string;
  veiculos?: any[];
  motoristas?: any[];
  equipamentos?: any[];
}

const ObraView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [obra, setObra] = useState<ObraDetalhada | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadObra = async () => {
    try {
      setIsLoading(true);

      // Obter headers padrão (inclui token automaticamente)
      const headers = getDefaultHeaders();

      const response = await fetch(buildApiUrl(`/obras/${id}/detalhada`), {
        method: 'GET',
        headers
      });

      if (response.status === 401) {
        setError('Sessão expirada. Por favor, faça login novamente.');
        setIsLoading(false);
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      if (response.status === 403) {
        setError('Você não tem permissão para visualizar esta obra. Ela pode pertencer a outra empresa ou não existir.');
        setIsLoading(false);
        return;
      }

      if (response.status === 404) {
        setError('Obra não encontrada. Ela pode ter sido excluída.');
        setIsLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setObra(data);
      setError(null);
    } catch (err) {
      console.error('Erro ao carregar obra:', err);
      setError('Erro ao carregar dados da obra. Verifique sua conexão e tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Intl.DateTimeFormat('pt-BR').format(new Date(dateString));
  };

  const formatDateTime = (dateString: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusClass = (status: string): string => {
    const statusMap: Record<string, string> = {
      'planejada': 'status-pending',
      'planejamento': 'status-pending',
      'em_andamento': 'status-active',
      'paralisada': 'status-warning',
      'finalizada': 'status-success',
      'cancelada': 'status-inactive'
    };
    return statusMap[status] || 'status-pending';
  };

  const getStatusText = (status: string): string => {
    const statusMap: Record<string, string> = {
      'planejada': 'Planejada',
      'planejamento': 'Planejamento',
      'em_andamento': 'Em Andamento',
      'paralisada': 'Paralisada',
      'finalizada': 'Finalizada',
      'cancelada': 'Cancelada'
    };
    return statusMap[status] || status;
  };

  useEffect(() => {
    loadObra();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (isLoading) {
    return (
      <ViewLayout
        title="Carregando Obra..."
        isLoading={true}
        onBack={() => navigate('/obras')}
      >
        <div></div>
      </ViewLayout>
    );
  }

  if (error || !obra) {
    return (
      <ViewLayout
        title="Erro ao Carregar"
        onBack={() => navigate('/obras')}
      >
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: '50px 20px' }}>
            <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: '#e74c3c', marginBottom: '20px' }}></i>
            <h3>{error || 'Obra não encontrada'}</h3>
            <p>A obra solicitada não pôde ser carregada.</p>
            <button className="btn btn-primary" onClick={() => navigate('/obras')}>
              Voltar para Lista
            </button>
          </div>
        </div>
      </ViewLayout>
    );
  }

  // Garantir que obra existe antes de acessar suas propriedades
  if (!obra) {
    return null;
  }

  const progressoTotal = obra.percentual_conclusao || 0;
  const valorTotal = obra.valor_total || 0;

  return (
    <ViewLayout
      title="Detalhes da Obra"
      subtitle={obra.nome}
      entityId={obra.id?.toString() || ''}
      onBack={() => navigate('/obras')}
      editUrl={`/obras/editar/${obra.id}`}
    >
      {/* Progresso */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-body">
          <h3 style={{ marginBottom: '15px', color: '#2c3e50' }}>Progresso da Obra</h3>
          <div style={{
            width: '100%',
            height: '30px',
            backgroundColor: '#e0e0e0',
            borderRadius: '15px',
            overflow: 'hidden',
            marginBottom: '10px'
          }}>
            <div style={{
              width: `${Math.min(progressoTotal, 100)}%`,
              height: '100%',
              backgroundColor: progressoTotal === 100 ? Config.THEME.SUCCESS_COLOR : Config.THEME.PRIMARY_COLOR,
              transition: 'width 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              paddingRight: '10px',
              color: 'white',
              fontWeight: 'bold'
            }}>
              {Math.round(progressoTotal)}%
            </div>
          </div>
        </div>
      </div>

      {/* Informações Básicas */}
      <div className="card">
        <div className="card-body">
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
              Informações Básicas
            </h3>

            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
              <div className="info-item">
                <label className="form-label">Número do Contrato</label>
                <span className="info-value">{obra.numero_contrato}</span>
              </div>

              <div className="info-item">
                <label className="form-label">Número da Obra</label>
                <span className="info-value">{obra.numero_obra}</span>
              </div>

              <div className="info-item">
                <label className="form-label">Categoria</label>
                <span className="info-value">{obra.categoria}</span>
              </div>

              <div className="info-item">
                <label className="form-label">Status</label>
                <span className="info-value">
                  <span className={`status-badge ${getStatusClass(obra.status)}`}>
                    {getStatusText(obra.status)}
                  </span>
                </span>
              </div>
            </div>

            {obra.descricao && (
              <div className="info-item" style={{ marginTop: '20px' }}>
                <label className="form-label">Descrição</label>
                <p style={{ color: '#7f8c8d', lineHeight: '1.6', margin: 0, padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {obra.descricao}
                </p>
              </div>
            )}
          </div>

          {/* Cliente */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
              Cliente
            </h3>

            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
              <div className="info-item">
                <label className="form-label">Nome</label>
                <span className="info-value">{obra.cliente_nome}</span>
              </div>

              <div className="info-item">
                <label className="form-label">CPF/CNPJ</label>
                <span className="info-value">{obra.cliente_cnpj || '-'}</span>
              </div>

              <div className="info-item">
                <label className="form-label">Telefone</label>
                <span className="info-value">{obra.cliente_contato || '-'}</span>
              </div>

              <div className="info-item">
                <label className="form-label">E-mail</label>
                <span className="info-value">{obra.cliente_email || '-'}</span>
              </div>

              <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Endereço</label>
                <span className="info-value">{obra.cliente_endereco || '-'}</span>
              </div>
            </div>
          </div>

          {/* Localização */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
              Localização da Obra
            </h3>

            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
              <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Endereço</label>
                <span className="info-value">
                  {obra.endereco_logradouro}{obra.endereco_bairro ? `, ${obra.endereco_bairro}` : ''}
                </span>
              </div>

              <div className="info-item">
                <label className="form-label">Cidade</label>
                <span className="info-value">{obra.endereco_cidade}</span>
              </div>

              <div className="info-item">
                <label className="form-label">Estado</label>
                <span className="info-value">{obra.endereco_estado}</span>
              </div>

              <div className="info-item">
                <label className="form-label">CEP</label>
                <span className="info-value">{obra.endereco_cep || '-'}</span>
              </div>

              {obra.latitude && obra.longitude && (
                <div className="info-item">
                  <label className="form-label">Coordenadas</label>
                  <span className="info-value">{obra.latitude.toFixed(6)}, {obra.longitude.toFixed(6)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Valores Financeiros */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
              Valores Financeiros
            </h3>

            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
              <div className="info-item">
                <label className="form-label">Valor Total</label>
                <span className="info-value" style={{ color: '#27ae60', fontWeight: 'bold', fontSize: '1.1em' }}>
                  {formatCurrency(valorTotal)}
                </span>
              </div>

              <div className="info-item">
                <label className="form-label">Moeda</label>
                <span className="info-value">{obra.moeda || 'BRL'}</span>
              </div>

              <div className="info-item">
                <label className="form-label">Valor Real Executado</label>
                <span className="info-value" style={{ color: '#9b59b6', fontWeight: 'bold' }}>
                  {formatCurrency(obra.valor_real || 0)}
                </span>
              </div>
            </div>
          </div>

          {/* Cronograma */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
              Cronograma
            </h3>

            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
              <div className="info-item">
                <label className="form-label">Início Previsto</label>
                <span className="info-value">{formatDate(obra.data_inicio_prevista)}</span>
              </div>

              <div className="info-item">
                <label className="form-label">Data Início Real</label>
                <span className="info-value">{formatDate(obra.data_inicio_real)}</span>
              </div>

              <div className="info-item">
                <label className="form-label">Conclusão Prevista</label>
                <span className="info-value">{formatDate(obra.data_fim_prevista)}</span>
              </div>

              <div className="info-item">
                <label className="form-label">Data Conclusão Real</label>
                <span className="info-value">{formatDate(obra.data_fim_real)}</span>
              </div>
            </div>
          </div>

          {/* Responsável */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
              Responsável Técnico
            </h3>

            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
              <div className="info-item">
                <label className="form-label">Nome</label>
                <span className="info-value">{obra.responsavel_tecnico_nome || '-'}</span>
              </div>

              <div className="info-item">
                <label className="form-label">Contato</label>
                <span className="info-value">{obra.responsavel_tecnico_contato || '-'}</span>
              </div>
            </div>
          </div>

          {/* Recursos Associados */}
          {((obra.veiculos && obra.veiculos.length > 0) ||
           (obra.motoristas && obra.motoristas.length > 0) ||
           (obra.equipamentos && obra.equipamentos.length > 0)) && (
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                Recursos Associados à Obra
              </h3>

              {obra.veiculos && obra.veiculos.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#34495e', marginBottom: '10px' }}>
                    <i className="fas fa-truck" style={{ marginRight: '8px' }}></i>
                    Veículos
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>
                    {obra.veiculos.map((veiculo, index) => (
                      <div key={index} style={{
                        padding: '15px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        backgroundColor: '#f8f9fa'
                      }}>
                        <strong style={{ display: 'block', marginBottom: '5px' }}>
                          {veiculo.modelo || veiculo.placa}
                        </strong>
                        <small style={{ color: '#7f8c8d' }}>Placa: {veiculo.placa}</small>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {obra.motoristas && obra.motoristas.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#34495e', marginBottom: '10px' }}>
                    <i className="fas fa-user" style={{ marginRight: '8px' }}></i>
                    Motoristas
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>
                    {obra.motoristas.map((motorista, index) => (
                      <div key={index} style={{
                        padding: '15px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        backgroundColor: '#f8f9fa'
                      }}>
                        <strong style={{ display: 'block', marginBottom: '5px' }}>{motorista.nome}</strong>
                        <small style={{ color: '#7f8c8d' }}>CPF: {motorista.cpf}</small>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {obra.equipamentos && obra.equipamentos.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#34495e', marginBottom: '10px' }}>
                    <i className="fas fa-tools" style={{ marginRight: '8px' }}></i>
                    Equipamentos
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>
                    {obra.equipamentos.map((equipamento, index) => (
                      <div key={index} style={{
                        padding: '15px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        backgroundColor: '#f8f9fa'
                      }}>
                        <strong style={{ display: 'block', marginBottom: '5px' }}>{equipamento.nome}</strong>
                        <small style={{ color: '#7f8c8d' }}>Tipo: {equipamento.tipo}</small>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Observações */}
          {obra.observacoes && (
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                Observações
              </h3>
              <div className="info-item">
                <p style={{ color: '#7f8c8d', lineHeight: '1.6', margin: 0, padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {obra.observacoes}
                </p>
              </div>
            </div>
          )}

          {/* Informações de Auditoria */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
              Informações de Auditoria
            </h3>

            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
              <div className="info-item">
                <label className="form-label">Criado em</label>
                <span className="info-value">{formatDateTime(obra.data_cadastro)}</span>
              </div>

              {obra.data_atualizacao !== obra.data_cadastro && (
                <div className="info-item">
                  <label className="form-label">Atualizado em</label>
                  <span className="info-value">{formatDateTime(obra.data_atualizacao)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ViewLayout>
  );
};

export default ObraView;
